import { useEffect, useState, useCallback } from "react";
import { X, ImageOff, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { PageHeader, useTitle } from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";
import { api, asset, thumb } from "@/lib/api";
import { useSiteConfig } from "@/context/SiteConfig";
import { IMAGES } from "@/data/content";

export default function Gallery() {
  useTitle("Gallery | Aurora Springs RP | RedM Roleplay", "Screenshots and community moments from Aurora Springs RP — Wild West roleplay scenes captured on our RedM server.");
  const { img } = useSiteConfig();
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);
  const [cat, setCat] = useState("all");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/gallery"); setItems(data); }
    catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const all = items || [];
  const categories = [...new Set(all.map((i) => i.category).filter(Boolean))].sort();
  const shown = cat === "all" ? all : all.filter((i) => i.category === cat);
  const featured = shown.filter((i) => i.featured);
  const rest = shown.filter((i) => !i.featured);
  const caption = (im) => im.title || "";

  return (
    <>
      <PageHeader overline="Moments from the frontier" title="Gallery" image={img("gallery", IMAGES.goldRider)}
        sub="A curated collection of screenshots and stories from life in Aurora Springs." />
      <section className="mx-auto max-w-7xl px-6 sm:px-10 py-16">
        {items === null && <div className="py-20 text-center font-mono text-sm text-[#8F877C]">Loading gallery…</div>}
        {items !== null && items.length === 0 && (
          <div data-testid="gallery-empty" className="border border-dashed border-[#2A201A] py-24 text-center">
            <ImageOff className="mx-auto h-8 w-8 text-[#5c554c]" />
            <p className="mt-4 text-[#8F877C]">The gallery is being curated. Check back soon.</p>
          </div>
        )}

        {items !== null && items.length > 0 && categories.length > 0 && (
          <div className="mb-10 flex flex-wrap gap-2" data-testid="gallery-filter-tabs">
            {["all", ...categories].map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`gallery-tab-${c === "all" ? "all" : c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`font-feature text-xs uppercase tracking-[0.2em] px-4 py-2.5 border transition-colors ${cat === c ? "border-teal bg-teal text-obsidian" : "border-[#2A201A] text-[#8F877C] hover:border-teal/50 hover:text-bone"}`}>
                {c === "all" ? "All" : c}
              </button>
            ))}
          </div>
        )}

        {featured.length > 0 && (
          <div className="mb-14">
            <Overline>Featured</Overline>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map((im, i) => (
                <Reveal key={im.id} delay={(i % 3) * 0.05}>
                  <div role="button" tabIndex={0} onClick={() => setActive(im)} data-testid={`gallery-featured-${i}`}
                    className="group relative block w-full cursor-pointer overflow-hidden border border-gold/40 hover:border-gold aspect-[4/3] transition-colors">
                    <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider bg-gold text-obsidian px-2 py-1"><Star className="h-3 w-3" fill="currentColor" /> Featured</span>
                    <img src={thumb(im.url, 800)} alt={caption(im)} loading="lazy" decoding="async"
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {caption(im) && <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-obsidian/90 to-transparent p-3 pt-8 opacity-0 group-hover:opacity-100 transition-opacity"><p className="font-mono text-xs text-bone">{caption(im)}</p></div>}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {rest.length > 0 && (
          <>
            {featured.length > 0 && <Overline>All moments</Overline>}
            <div className={`columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance] ${featured.length > 0 ? "mt-6" : ""}`}>
              {rest.map((im, i) => (
                <Reveal key={im.id} delay={(i % 3) * 0.05} className="mb-4 break-inside-avoid">
                  <div role="button" tabIndex={0} onClick={() => setActive(im)} data-testid={`gallery-item-${i}`}
                    className="group relative block w-full cursor-pointer overflow-hidden border border-[#2A201A] hover:border-teal/50 transition-colors">
                    <img src={thumb(im.url, 800)} alt={caption(im)} loading="lazy" decoding="async"
                      className="w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-obsidian/0 group-hover:bg-obsidian/20 transition-colors" />
                    {caption(im) && <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-obsidian/90 to-transparent p-3 pt-8 opacity-0 group-hover:opacity-100 transition-opacity"><p className="font-mono text-xs text-bone">{caption(im)}</p></div>}
                  </div>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </section>

      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-obsidian/95 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setActive(null)} data-testid="gallery-lightbox">
            <button className="absolute top-6 right-6 text-bone hover:text-teal" onClick={() => setActive(null)} data-testid="lightbox-close"><X className="h-7 w-7" /></button>
            <motion.img initial={{ scale: 0.92 }} animate={{ scale: 1 }} exit={{ scale: 0.92 }}
              src={asset(active.url)} alt={caption(active)}
              className="max-h-[85vh] max-w-full object-contain border border-[#2A201A]" onClick={(e) => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
